import React from "react";
import type { AlternateSelf } from "@/lib/infiniteSelves";
export interface LifeSwitcherProps {
  allSelves: AlternateSelf[];
  activeIndex: number;
  onSwitchSelf: (index: number) => void;
  cardHue: number;
}
export default function LifeSwitcher({
  allSelves,
  activeIndex,
  onSwitchSelf,
  cardHue
}: LifeSwitcherProps): JSX.Element {
  if (!allSelves || allSelves.length <= 1) {
    return <></>;
  }
  const prevIndex = (activeIndex - 1 + allSelves.length) % allSelves.length;
  const nextIndex = (activeIndex + 1) % allSelves.length;
  return <div role="region" aria-label="Alternate selves switcher" className="p-2 sm:p-2.5 rounded-xl bg-card/85 border border-border/80 shadow-inner flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5">
      {/* 4 Persona Pills */}
      <div role="tablist" aria-label="Select alternate self" className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 flex-1">
        {allSelves.map((s, idx) => {
        const isActive = idx === activeIndex;
        const hue = s.visual?.hue ?? cardHue;
        return <button key={s.timelineId || s.archetype || idx} type="button" role="tab" aria-selected={isActive} aria-current={isActive ? "true" : undefined} onClick={() => onSwitchSelf(idx)} className={`min-h-[44px] px-2.5 py-2 rounded-lg text-left transition-all duration-200 border cursor-pointer flex flex-col justify-center min-w-0 ${isActive ? "bg-card border-primary text-foreground ring-1 ring-primary/60 shadow-[0_0_14px_var(--glow-primary)]" : "bg-muted/40 border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/70 hover:border-border"}`} style={isActive ? {
          borderColor: `hsl(${hue} 100% 55%)`,
          boxShadow: `0 0 16px -2px hsl(${hue} 100% 55% / 0.45)`
        } : undefined}>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="size-2 rounded-full shrink-0" style={{
              backgroundColor: `hsl(${hue} 100% 55%)`
            }} aria-hidden="true" />
                <span className="text-[10px] font-mono uppercase tracking-wider font-semibold truncate text-primary">
                  {s.scenarioLabel ? s.scenarioLabel.replace(/^A |^An /i, "") : `Self #${idx + 1}`}
                </span>
              </div>
              <span className="text-xs font-serif font-bold truncate text-foreground/95 block mt-0.5">
                {s.name}
              </span>
            </button>;
      })}
      </div>

      {/* Prev / Next Steppers */}
      <div className="flex items-center justify-end gap-1.5 shrink-0 self-end md:self-auto border-t md:border-t-0 md:border-l border-border/60 pt-2 md:pt-0 md:pl-2.5 w-full md:w-auto">
        <button type="button" onClick={() => onSwitchSelf(prevIndex)} className="ql-btn-ghost min-h-[44px] px-3 text-xs flex-1 md:flex-initial justify-center" aria-label={`Previous self: ${allSelves[prevIndex]?.name ?? "Previous"}`}>
          <span aria-hidden="true" className="mr-1 font-mono">←</span>
          <span>Previous</span>
        </button>
        <button type="button" onClick={() => onSwitchSelf(nextIndex)} className="ql-btn-ghost min-h-[44px] px-3 text-xs flex-1 md:flex-initial justify-center" aria-label={`Next self: ${allSelves[nextIndex]?.name ?? "Next"}`}>
          <span>Next</span>
          <span aria-hidden="true" className="ml-1 font-mono">→</span>
        </button>
      </div>
    </div>;
}